/**
 * ADR 023 (BL-677) — the shared, pure `LotPhase.kind` display predicates, in one
 * home next to the `LotPhase` type + `deriveLotPhase`. Lifted out of the two
 * apps' phase-display helpers (bidder `lot-bid-panel-display.ts` + admin
 * `timed-controls/lot-phase-display.ts`) per sharing.md §1 (no cross-app
 * copy-paste) — the literal `isLotPhaseActive` ≡ admin `isTimedLotBiddingActive`
 * and `isLotPhaseTerminal` ≡ admin `isTimedLotMenuHidden` duplicates collapse here.
 *
 * These are IsomorphicServices (ADR 010 / sharing.md §3): pure, no I/O, no auth
 * enforcement, client-safe (nothing server-only transits), safe inside
 * `useMemo`. Written as plain functions matching every sibling in this folder
 * (`deriveLotPhase`, `deriveTerminalDisposition`, `deriveAuctionPhase`).
 *
 * BL-1015: every predicate here is typed on the MINIMAL `{ kind: LotPhaseKind }`
 * shape rather than the full stamped {@link LotPhase} union — sharing.md §3
 * rule 2 (*generic on input shape, not on row type*), the precedent
 * {@link lotPhaseTerminalKind} set below. Each body only ever reads `.kind`, so
 * the wider type bought nothing while forcing a caller holding just a kind (the
 * bidder grid card) to fabricate timestamps, or to cast. A full `LotPhase` still
 * satisfies this structurally, so every pre-existing call site is unaffected.
 *
 * App-specific bits stay local: the bidder snapshot→input mapper
 * (`lotSnapshotToLotPhaseInput`) + countdown/price display, and admin's
 * menu-hidden wrapper (`isTimedLotMenuHidden`).
 */
import type { LotPhaseKind } from './phase-tokens';

/**
 * Direct-bid CTA gate: the bidding window is open (`now < current_close_time`).
 * Pre-ADR-023 this was `lot.status === 'active'` — but the stored `'active'`
 * ALSO covered the closed-but-not-yet-resolved gap, which the predicate splits
 * into `open-for-bidding` and `awaiting-resolution`. For an AUTO-close lot the
 * CTA must disable the instant the window closes, so it keys on this narrow
 * kind. Manual-close "Last call" is handled at the call site.
 */
export function isLotPhaseOpenForBidding(phase: { kind: LotPhaseKind }): boolean {
  return phase.kind === 'open-for-bidding';
}

/**
 * Past the scheduled close but not yet resolved (no terminal stamp), on an
 * AUTO-close auction — the lot is effectively closed to bidding. (BUG-909:
 * the manual-close analog of this state is the distinct `last-call` kind,
 * which stays biddable — see {@link isLotPhaseLastCall}.)
 */
export function isLotPhaseAwaitingResolution(phase: { kind: LotPhaseKind }): boolean {
  return phase.kind === 'awaiting-resolution';
}

/**
 * BUG-909: past the scheduled close but not yet resolved, on a MANUAL-close
 * auction — the lot stays biddable ("Last call") until a clerk resolves it.
 * The manual-close analog of {@link isLotPhaseAwaitingResolution}.
 */
export function isLotPhaseLastCall(phase: { kind: LotPhaseKind }): boolean {
  return phase.kind === 'last-call';
}

/**
 * True for any phase kind that still accepts direct bids — the union of the
 * before-close window (`open-for-bidding`) and the manual-close grace window
 * (`last-call`, BUG-909). Single source for "should the bid CTA / bid-gate
 * accept this lot right now", so a new consumer can't reproduce BUG-909 by
 * re-deriving `closingType === 'manual'` itself.
 */
export function isLotPhaseBiddable(phase: { kind: LotPhaseKind }): boolean {
  return phase.kind === 'open-for-bidding' || phase.kind === 'last-call';
}

/**
 * The pre-ADR-023 stored `'active'` sense = `open-for-bidding` OR
 * `awaiting-resolution` OR `last-call` (the DB only flipped to a terminal
 * status once the close cron ran). Shared across the bidder CTA and the
 * admin clerk dashboard (was admin `isTimedLotBiddingActive`) — the one
 * genuine literal duplicate BL-677 collapses. BUG-909 added `last-call`
 * (the manual-close analog of `awaiting-resolution`) to the union.
 */
export function isLotPhaseActive(phase: { kind: LotPhaseKind }): boolean {
  return (
    phase.kind === 'open-for-bidding' ||
    phase.kind === 'awaiting-resolution' ||
    phase.kind === 'last-call'
  );
}

/** Pre-bidding: max-bids only, direct bids blocked (was `status === 'pre_bidding'`). */
export function isLotPhasePreBidding(phase: { kind: LotPhaseKind }): boolean {
  return phase.kind === 'pre-bidding';
}

/** Paused (was the lot-level `status === 'paused'` term). */
export function isLotPhasePaused(phase: { kind: LotPhaseKind }): boolean {
  return phase.kind === 'paused';
}

/**
 * Terminal set matching the pre-ADR-023 `isTerminalLotStatus` union
 * (`sold` / `no_sale` / `unsold` / `withdrawn` / `deleted`). The
 * predicate has no separate `no_sale` — it folds into `unsold`. Shared: admin's
 * `isTimedLotMenuHidden` (hide the row action menu) delegates to this.
 */
export function isLotPhaseTerminal(phase: { kind: LotPhaseKind }): boolean {
  return (
    phase.kind === 'sold' ||
    phase.kind === 'unsold' ||
    phase.kind === 'withdrawn' ||
    phase.kind === 'deleted'
  );
}

/**
 * BL-1769 — the lot was REMOVED (soft-deleted), not resolved. A strict subset
 * of {@link isLotPhaseTerminal}: `deleted` is terminal in the lifecycle sense
 * (it takes no more bids and no further transition), but it is the one terminal
 * kind whose sale did NOT happen — which is why {@link lotPhaseTerminalKind}
 * has always returned `undefined` for it while labelling the other three.
 *
 * Buyer-facing surfaces MUST branch on this BEFORE the terminal branch. Folded
 * into `terminal`, a pulled lot renders the generic closed treatment ("Bidding
 * closed"), which tells the buyer *the sale happened without me* — a different
 * and untrue fact. Kept OUT of `isLotPhaseTerminal` deliberately: that
 * predicate gates admin transition guards, batch financial gates and
 * biddability, all of which must keep treating a deleted lot as terminal.
 */
export function isLotPhaseRemoved(phase: { kind: LotPhaseKind }): boolean {
  return phase.kind === 'deleted';
}

/**
 * Coming-soon = visible but not yet open (pre-open prep). Mirrors the bidder
 * coming-soon set (`draft` / `ready`) so the detail panel and the card grid
 * can't drift on which lots are coming-soon. BL-1015 made the grid card call
 * this directly, retiring its local `PREVIEW_LOT_STATUSES` copy. (BL-772 retired the separate
 * `published` phase — a bidding-opened-but-not-open lot now derives `ready`.)
 */
export function isLotPhaseComingSoon(phase: { kind: LotPhaseKind }): boolean {
  return phase.kind === 'draft' || phase.kind === 'ready';
}

/**
 * The terminal-outcome LABEL kind — `sold` / `withdrawn` get their own label;
 * `unsold` AND `passed` both fold to `'unsold'` ("Not Sold") since a passed
 * lot (live-auction "no takers") is, by construction, always a zero-bid
 * closure — the same outcome `unsold`'s `reason: 'no-bids'` describes for
 * timed lots. `deleted` (and every non-terminal kind) returns `undefined`.
 *
 * BUGFIX (BUG-909): `passed` previously returned `undefined` here, forcing
 * every consumer of `resolveLotTerminalLabel` (`lot-outcome-label.ts`) to
 * fall back to inferring sold-vs-not from a raw `currentBid` NUMBER instead
 * of trusting the already-authoritative phase. That inference is a footgun —
 * a caller building a DISPLAY-oriented bid value (falling back to the
 * opening/starting price when there are no real bids, e.g. the grid card's
 * `effectiveBid`) unintentionally reads as "there was a winning bid" and
 * mislabels a passed lot "Sold". Folding `passed` here removes the need for
 * that inference entirely for this kind.
 *
 * This function set the MINIMAL-`{ kind }` typing precedent the whole file now
 * follows (see the module docblock) — one shared function instead of three
 * independent copies of the same mapping (`resolveTerminalKind` in bidder's
 * `lot-card-item.ts`, and the inline ternary in `public-auctions-mappers.ts`,
 * both migrated to delegate here). BL-1015 removed the last caller that reached
 * it through a status string + `as LotPhaseKind` cast; the bidder grid card now
 * passes its derived kind straight through.
 */
export function lotPhaseTerminalKind(phase: { kind: LotPhaseKind }): 'sold' | 'unsold' | 'withdrawn' | undefined {
  if (phase.kind === 'sold') return 'sold';
  if (phase.kind === 'unsold') return 'unsold';
  if (phase.kind === 'withdrawn') return 'withdrawn';
  return undefined;
}
